// ⑥ 연출컷: 뽑기, 판정, 고치기
import { ASPECTS, FIX_TYPES, type ModelKey } from "../../shared/knowledge";
import { activeConstraints, baseImage, MAX_ANGLES } from "../../shared/prompt";
import type { Cut, Project } from "../../shared/types";
import { icon } from "../icons";
import { announce, api, busy, cardHead, chipGroup, clear, emptyState, fileName, fmtDate, fmtDay, genState, h, mediaUrl, menu, modal, nudgeQueued, openViewer, pickFiles, sceneLine, segmented, shrinkImage, toast, withCost, type Child, type ViewerItem } from "../lib";
import { refreshMe, state } from "../store";
import { openFixDialog } from "./fix-dialog";
import type { Ctx } from "./project";

const MODEL_KO: Record<string, string> = { fast: "기본", pro: "정밀" };
const VERDICTS = [
  { id: "use", ko: "쓴다", short: "쓴다", icon: "check" as const },
  { id: "fix", ko: "보정하면 쓴다", short: "보정", icon: "pencil" as const },
  { id: "no", ko: "못 쓴다", short: "못 씀", icon: "x" as const },
];

let pollTimer: any = null;
const pad = (n: number) => String(n).padStart(2, "0");

export async function renderCuts(body: HTMLElement, ctx: Ctx) {
  clearTimeout(pollTimer);
  let cuts = await api<Cut[]>(`/api/projects/${ctx.p.id}/cuts`);
  const admin = Boolean(state.me?.admin);
  const panel = h("div", { class: "card" });
  const galleryHead = h("div");
  const gallery = h("div", { class: "cut-grid" });

  // ── 뽑기 설정 ──
  let model: ModelKey = "fast";
  let size = "2K";
  let aspect = "16:9";
  let count = 2;
  const credits = (m: string, s: string) => state.config?.models[m]?.credits[s] ?? 1;

  function refCount(p: Project) {
    const base = baseImage(p.data);
    let n = base ? 1 + Math.min(MAX_ANGLES, p.data.space.length - 1) : 0;
    if (p.data.scene.continuity && p.data.canonCutId) n++;
    return n;
  }

  /** 만든 순서대로 번호 (오래된 것이 01) */
  function numbers(): Map<string, number> {
    const m = new Map<string, number>();
    [...cuts].sort((a, b) => a.createdAt.localeCompare(b.createdAt)).forEach((c, i) => m.set(c.id, i + 1));
    return m;
  }
  const cutTitle = (c: Cut, nums: Map<string, number>) => `컷 ${pad(nums.get(c.id) || 0)}${c.kind === "fix" ? ` · 고친 컷 ${c.depth}차` : ""}`;
  /** 내려받는 파일 이름: 화면에 보이는 번호와 같게 (예: '강변 페스티벌_컷04.png') */
  const dlName = (c: Cut, nums: Map<string, number>) => fileName(`${ctx.p.title}_컷${pad(nums.get(c.id) || 0)}`, c.mediaKey || "");

  const info = h("p", { class: "info-line small muted" });
  function drawInfo() {
    const p = ctx.p;
    clear(info);
    info.append(
      h("span", { class: "row tight", title: "그림에 함께 보내는 장소 그림·승인본 수" }, icon("image", 16), `참고 그림 ${refCount(p)}`),
      h("span", { class: "row tight", title: "모든 그림에 그대로 들어가는 조건 수" }, icon("list-check", 16), `지키는 조건 ${activeConstraints(p.data).length}`),
      h("span", { class: "row tight", title: "남은 크레딧" }, icon("coins", 16), `남은 ${state.me?.credits ?? 0}`),
    );
  }

  function drawPanel() {
    clear(panel);
    const tool = (label: Child, control: HTMLElement, note?: string) => h("div", { class: "tool" }, h("span", { class: "tool-label" }, label), control, note ? h("span", { class: "tool-note xs muted" }, note) : null);
    const aspectSel = h("select", { "aria-label": "비율" }, ASPECTS.map((a) => h("option", { value: a, selected: a === aspect }, a === "16:9" ? "16:9 · PPT 한 장" : a)));
    aspectSel.addEventListener("change", () => (aspect = aspectSel.value));
    panel.append(
      cardHead("뽑기 설정", { eyebrow: "p2", help: `기본: 빠르고 크레딧이 적게 들어요 (${credits("fast", "2K")} 크레딧) · 정밀: 글자·로고가 중요한 최종본 (${credits("pro", "2K")} 크레딧)` }),
      h(
        "div",
        { class: "toolbar" },
        tool(
          "모델",
          segmented(
            Object.keys(state.config?.models || { fast: 1, pro: 1 }).map((k) => ({ id: k, ko: MODEL_KO[k] || state.config?.models[k]?.ko || k })),
            model,
            (v) => {
              model = (v || "fast") as ModelKey;
              drawBar();
            },
            "모델",
          ),
        ),
        tool(
          "크기",
          segmented(
            ["1K", "2K", "4K"].map((s) => ({ id: s, ko: s })),
            size,
            (v) => {
              size = v || "2K";
              drawBar();
            },
            "크기",
          ),
          "2K · 제안서 기본",
        ),
        tool("비율", aspectSel),
        tool(
          "장수",
          segmented(
            ["1", "2", "3", "4"].map((n) => ({ id: n, ko: n })),
            String(count),
            (v) => {
              count = Number(v || 2);
              drawBar();
            },
            "장수",
          ),
        ),
      ),
      info,
    );
    drawInfo();
  }

  function drawBar() {
    const p = ctx.p;
    const pending = p.data.elements.filter((e) => e.status === "proposed").length;
    const need = credits(model, size) * count;
    const have = state.me?.credits ?? 0;
    const go = h("button", { class: "btn primary", type: "button", disabled: pending > 0 || need > have }, withCost(`${count}장 뽑기`, need));
    go.addEventListener("click", () =>
      busy(go, "보내는 중", async () => {
        const made = await api<Cut[]>(`/api/projects/${p.id}/cuts`, { body: { model, size, aspect, count } });
        cuts = [...made, ...cuts];
        await refreshMe();
        ctx.set(await api<Project>(`/api/projects/${p.id}`));
        drawInfo();
        drawBar();
        drawGallery();
        poll();
        toast("연출컷을 그리기 시작했어요", "info");
      }),
    );
    const sc = p.data.scene;
    let status: Child;
    let warn = false;
    if (pending) {
      status = [`확인하지 않은 요소 ${pending}개 · `, h("a", { href: `#/p/${p.id}/elements` }, "요소 합의로")];
      warn = true;
    } else if (need > have) {
      status = `크레딧이 모자라요 (필요 ${need} · 남은 ${have})`;
      warn = true;
    } else if (!sc.scene) {
      status = "장면을 고르지 않았어요";
      warn = true;
    } else if (!p.data.space.length && !p.data.elements.length) {
      status = "장소 그림과 요소가 없어요";
      warn = true;
    } else status = sceneLine(sc);
    ctx.bar({ status, warn, primary: go });
  }

  // ── 갤러리 ──
  function drawGallery() {
    const nums = numbers();
    clear(galleryHead);
    galleryHead.append(h("h2", { class: "section-title" }, "연출컷", h("span", { class: "count num" }, String(cuts.length))));
    clear(gallery);
    if (!cuts.length) {
      gallery.append(emptyState({ icon: "film", title: "아직 연출컷이 없어요", body: "처음엔 2장을 뽑아 비교해 보세요" }));
      return;
    }
    for (const c of cuts) gallery.append(cutCard(c, nums));
  }

  // 판정은 한 번에 하나씩 차례로 보낸다. 응답 순서가 뒤바뀌어 화면과 서버가 어긋나지 않게, 마지막 것만 화면에 반영한다
  let verdictChain: Promise<unknown> = Promise.resolve();
  let verdictSeq = 0;
  function setVerdict(c: Cut, v: string | null): Promise<void> {
    const my = ++verdictSeq;
    const run = async () => {
      try {
        const upd = await api<Cut>(`/api/projects/${ctx.p.id}/cuts/${c.id}/verdict`, { body: { verdict: v } });
        cuts = cuts.map((x) => (x.id === c.id ? upd : x));
        const fresh = await api<Project>(`/api/projects/${ctx.p.id}`);
        if (my !== verdictSeq) return;
        ctx.set(fresh);
        if (v === "use") toast("승인본으로 정했어요", "ok");
        drawGallery();
        drawInfo();
      } catch (err) {
        toast(err instanceof Error ? err.message : String(err), "err");
        if (my === verdictSeq) drawGallery();
      }
    };
    const next = verdictChain.then(run);
    verdictChain = next;
    return next;
  }

  /** 판정 나눔 단추. 화살표는 초점만 옮기고 Space·Enter 로 정한다(누를 때마다 서버에 가므로). 다시 그린 뒤 같은 단추로 초점을 돌려준다 */
  const verdictSeg = (c: Cut, onDone?: () => void) => {
    const seg: HTMLElement = segmented(
      VERDICTS.map((v) => ({ id: v.id, icon: v.icon, ko: [h("span", { class: "hide-sm" }, v.ko), h("span", { class: "show-sm" }, v.short)], title: v.ko })),
      c.verdict ?? null,
      (v) => {
        const a = document.activeElement as HTMLElement | null;
        const focusV = a && seg.contains(a) ? a.dataset.v : undefined;
        const inViewer = Boolean(seg.closest(".viewer"));
        void setVerdict(c, v).then(() => {
          onDone?.();
          if (!focusV) return;
          const scope = inViewer ? document.querySelector(".viewer") : gallery.querySelector(`[data-cut="${c.id}"]`);
          scope?.querySelector<HTMLElement>(`.seg.verdict [data-v="${focusV}"]`)?.focus({ preventScroll: true });
        });
      },
      "판정",
      { cls: "verdict", allowNone: true, manual: true },
    );
    return seg;
  };

  async function toSpace(c: Cut) {
    try {
      ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/cuts/${c.id}/to-space`, { body: { label: "합의한 구조 그림" } }));
      toast("장소 그림에 넣었어요", "ok");
      drawInfo();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), "err");
    }
  }

  function cutCard(c: Cut, nums: Map<string, number>): HTMLElement {
    const isCanon = ctx.p.data.canonCutId === c.id;
    const title = cutTitle(c, nums);
    const done = c.status === "done" && c.mediaKey;
    const full = c.constraintCount === c.constraintTotal;
    const showModel = !(c.model === "fast" && c.size === "2K");
    return h(
      "article",
      { class: `card cut-card p2${isCanon ? " canon" : ""}`, "data-cut": c.id },
      h(
        "div",
        { class: "thumb wide" },
        done ? h("img", { src: mediaUrl(c.mediaKey!), alt: `연출컷 ${pad(nums.get(c.id) || 0)}`, loading: "lazy", onclick: () => viewer(c) }) : genState(c, "연출컷"),
        h("span", { class: "chip dark thumb-tag" }, `컷 ${pad(nums.get(c.id) || 0)}`),
        isCanon ? h("span", { class: "thumb-badge" }, icon("star", 12), "승인본") : null,
      ),
      h(
        "div",
        { class: "cut-meta" },
        h("div", { class: "cut-title" }, h("b", null, title), h("span", { class: "xs muted num", title: fmtDate(c.createdAt) }, fmtDay(c.createdAt))),
        h(
          "div",
          { class: "row wrap tight" },
          c.constraintTotal ? (full ? h("span", { class: "chip ok", title: "이번에 실린 조건 / 그때 살아 있던 조건" }, icon("check", 12), "조건 모두 반영") : h("span", { class: "chip warn", title: "이번에 실린 조건 / 그때 살아 있던 조건" }, `조건 ${c.constraintCount}/${c.constraintTotal} 반영`)) : null,
          showModel ? h("span", { class: "chip" }, `${MODEL_KO[c.model] || c.model} · ${c.size}`) : null,
        ),
        c.fixes?.length ? h("p", { class: "small muted one-line" }, "고친 곳: ", c.fixes.map((f) => `${f.label}(${FIX_TYPES.find((t) => t.id === f.type)?.ko || f.type})`).join(", ")) : null,
        done ? verdictSeg(c) : null,
        done
          ? h(
              "div",
              { class: "cut-actions" },
              h("button", { class: "btn small", type: "button", onclick: () => fixDialog(c) }, icon("pencil", 16), "고치기"),
              h("span", { class: "spacer" }),
              h("button", { class: "btn ghost small icon-only", type: "button", "aria-label": `${title} 크게 보기`, title: "크게 보기", onclick: () => viewer(c) }, icon("maximize", 16)),
              h("a", { class: "btn ghost small icon-only", href: mediaUrl(c.mediaKey!), download: dlName(c, nums), "aria-label": `${title} 내려받기`, title: "내려받기" }, icon("download", 16)),
              menu([{ label: "장소 그림으로 쓰기", icon: "image", onclick: () => void toSpace(c) }], `${title} 메뉴`),
            )
          : null,
      ),
    );
  }

  function viewer(c: Cut) {
    const nums = numbers();
    const list = cuts.filter((x) => x.status === "done" && x.mediaKey);
    const items: ViewerItem[] = list.map((x) => ({
      src: mediaUrl(x.mediaKey!),
      title: cutTitle(x, nums),
      // 뷰어 안에서 판정을 바꾸면 '승인본'도 바뀌므로 그릴 때마다 계산한다
      meta: () => `${fmtDate(x.createdAt)}${ctx.p.data.canonCutId === x.id ? " · 승인본" : ""}`,
      download: dlName(x, nums),
      actions: ({ close, refresh }) => {
        const cur = cuts.find((y) => y.id === x.id) || x;
        return [
          verdictSeg(cur, refresh),
          h(
            "button",
            {
              class: "btn",
              type: "button",
              onclick: () => {
                close();
                fixDialog(cur);
              },
            },
            icon("pencil", 16),
            "고치기",
          ),
          h("button", { class: "btn", type: "button", onclick: () => void toSpace(cur) }, icon("image", 16), "장소 그림으로 쓰기"),
        ];
      },
      details: admin
        ? () => [
            h("p", { class: "xs" }, `붙인 그림 ${x.refs.length}장 · 실린 조건 ${x.constraintCount}/${x.constraintTotal}`),
            h("ul", { class: "xs" }, x.refs.map((r) => h("li", null, `${r.label} (${r.role})`))),
            h("pre", { class: "prompt" }, x.prompt),
          ]
        : undefined,
    }));
    openViewer(items, Math.max(0, list.findIndex((x) => x.id === c.id)));
  }

  // ── 고치기 창 ──
  type FixValue = { elementId?: string; label: string; type: string; note: string; refKey?: string };
  function fixDialog(c: Cut) {
    const els = ctx.p.data.elements.filter((e) => e.status !== "proposed");
    const nums = numbers();
    openFixDialog<FixValue>({
      title: "컷 고치기",
      src: mediaUrl(c.mediaKey!),
      alt: cutTitle(c, nums),
      warn: c.depth >= 2 ? `벌써 ${c.depth}번 고쳤어요. 새로 뽑는 것도 좋아요` : undefined,
      help: "앞으로도 지켜야 할 사실(예: '원통은 LED')은 지키는 조건에 자동으로 쌓여요.",
      cost: credits(c.model, c.size),
      newRow: () => {
        const target = h("select", { "aria-label": "어디를" }, els.map((e) => h("option", { value: e.id }, e.name)), h("option", { value: "" }, "기타 (직접 적기)"));
        const other = h("input", { placeholder: "고칠 것 이름 (예: 바닥 로고)", maxlength: 60, "aria-label": "고칠 것 이름" });
        other.hidden = els.length > 0;
        target.addEventListener("change", () => (other.hidden = Boolean(target.value)));
        let type = "wrong";
        const types = chipGroup(FIX_TYPES, [type], false, (v) => (type = v[0]), "무엇이 문제인가요", { small: true });
        const note = h("textarea", { rows: 2, maxlength: 400, placeholder: "어떻게 고칠지 쉬운 말로. 예: 위아래로 내려오는 원통형 LED이고, 은하수 영상이 나와야 해요" }) as HTMLTextAreaElement;
        let refKey: string | undefined;
        const refSlot = h("div", { class: "ref-slot" });
        const drawRef = (label?: string, key?: string) => {
          clear(refSlot);
          if (key) {
            refSlot.append(
              h(
                "div",
                { class: "ref-chip" },
                h("img", { src: mediaUrl(key), alt: "" }),
                h("span", { class: "one-line small" }, label || "참고 그림"),
                h(
                  "button",
                  {
                    class: "btn ghost small icon-only",
                    type: "button",
                    "aria-label": "참고 그림 빼기",
                    onclick: () => {
                      refKey = undefined;
                      drawRef();
                    },
                  },
                  icon("x", 14),
                ),
              ),
            );
          } else {
            refSlot.append(
              h(
                "button",
                {
                  class: "btn small",
                  type: "button",
                  onclick: () =>
                    refPicker(c.id, (k, l) => {
                      refKey = k;
                      drawRef(l, k);
                    }),
                },
                icon("image", 16),
                "참고 그림 붙이기",
              ),
            );
          }
        };
        drawRef();
        return {
          body: [h("label", null, "어디를", target, other), h("div", { class: "field" }, h("span", { class: "field-label" }, "무엇이 문제인가요"), types), h("label", null, "어떻게", note), refSlot],
          value: () => {
            const el = els.find((e) => e.id === target.value);
            return { elementId: el?.id, label: el?.name || other.value.trim(), type, note: note.value.trim(), refKey };
          },
          focus: () => target.focus(),
        };
      },
      submit: async (fixes, close) => {
        if (fixes.some((f) => !f.label)) throw new Error("'기타'를 고른 곳은 이름을 적어 주세요.");
        if (fixes.some((f) => !f.note && f.type !== "remove")) throw new Error("어떻게 고칠지 한 줄 적어 주세요.");
        const r = await api<{ cut: Cut; added: { ko: string }[] }>(`/api/projects/${ctx.p.id}/cuts/${c.id}/fix`, { body: { fixes } });
        close();
        cuts = [r.cut, ...cuts];
        await refreshMe();
        ctx.set(await api<Project>(`/api/projects/${ctx.p.id}`));
        drawInfo();
        drawBar();
        drawGallery();
        poll();
        if (r.added.length) toast(`지키는 조건에 ${r.added.length}개를 더했어요: ${r.added.map((a) => a.ko).join(" / ")}`, "ok", 8000);
        else toast("컷을 고치기 시작했어요", "info");
      },
    });
  }

  /** 참고 그림: 새로 올리거나, 이 프로젝트의 컷·장소 그림 중에서 고른다 */
  function refPicker(exclude: string, onPick: (key: string, label: string) => void) {
    const nums = numbers();
    // 지금 고치고 있는 컷 자신은 참고 그림 후보에서 뺀다
    const cutChoices = cuts.filter((x) => x.status === "done" && x.mediaKey && x.id !== exclude).map((x) => ({ key: x.mediaKey!, label: cutTitle(x, nums) }));
    const spaceChoices = ctx.p.data.space.map((s) => ({ key: s.key, label: s.label }));
    const upload = h("button", { class: "btn", type: "button" }, icon("upload", 16), "새 그림 올리기");
    const grid = (list: { key: string; label: string }[]) =>
      h(
        "div",
        { class: "pick-grid" },
        list.map((x) =>
          h(
            "button",
            {
              class: "pick",
              type: "button",
              onclick: () => {
                onPick(x.key, x.label);
                close();
              },
            },
            h("img", { src: mediaUrl(x.key), alt: "", loading: "lazy" }),
            h("span", { class: "small one-line" }, x.label),
          ),
        ),
      );
    const close = modal(
      "참고 그림 고르기",
      h(
        "div",
        { class: "stack" },
        h("div", null, upload),
        cutChoices.length ? h("div", { class: "stack tight" }, h("h3", null, "이 프로젝트의 컷"), grid(cutChoices)) : null,
        spaceChoices.length ? h("div", { class: "stack tight" }, h("h3", null, "장소 그림"), grid(spaceChoices)) : null,
      ),
      { size: "lg", sub: "고칠 것이 제대로 나온 그림을 골라요. 그 모습만 참고해요" },
    );
    upload.addEventListener("click", async () => {
      const [f] = await pickFiles(false);
      if (!f) return;
      await busy(upload, "올리는 중", async () => {
        const { blob } = await shrinkImage(f);
        const r = await api<{ key: string }>(`/api/projects/${ctx.p.id}/refs`, { raw: blob, rawType: "image/jpeg" });
        onPick(r.key, f.name);
        close();
      });
    });
  }

  // ── 진행 확인 ──
  function poll() {
    clearTimeout(pollTimer);
    const waiting = cuts.filter((c) => c.status === "queued" || c.status === "running");
    if (!waiting.length || !document.body.contains(gallery)) return;
    pollTimer = setTimeout(async () => {
      if (!document.body.contains(gallery)) return;
      try {
        const fresh = await api<Cut[]>(`/api/projects/${ctx.p.id}/cuts`);
        // 백그라운드 작업이 시작되지 않은 채 20초가 지나면 직접 맡긴다(한 번만)
        nudgeQueued(fresh);
        const finished = fresh.filter((f) => f.status === "done" && cuts.find((c) => c.id === f.id)?.status !== "done").length;
        const changed = fresh.some((f) => cuts.find((c) => c.id === f.id)?.status !== f.status);
        cuts = fresh;
        if (changed) {
          drawGallery();
          if (finished) announce(`연출컷 ${finished}장이 다 됐어요`);
          if (fresh.some((c) => c.status === "failed")) {
            await refreshMe();
            drawInfo();
            drawBar();
          }
        }
      } catch {
        /* 다음 차례에 다시 본다 */
      }
      poll();
    }, 3000);
  }

  body.append(h("div", { class: "stack loose" }, panel, h("section", null, galleryHead, gallery)));
  drawPanel();
  drawBar();
  drawGallery();
  poll();
}
